<script setup lang="ts">
import { cardData } from '~/data/team';


const marqueeRef = ref<HTMLDivElement | null>(null);
const isTouch = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;

const centerOnce = () => {
  const el = marqueeRef.value;
  if (!el || !isTouch()) return;
  el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
};

onMounted(() => {
  requestAnimationFrame(centerOnce);
  setTimeout(centerOnce, 100);
});
</script>

<template>
  <section id="team" class="team-wrapper">
    <div class="team">
      <h2 class="title headline-2">Team</h2>
      <div class="marquee" ref="marqueeRef">
        <div class="cards-wrapper">
          <div v-for="(card, index) in cardData" :key="card.name" class="card">
            <nuxt-img :src="card.image" class="card-image" :class="{'img-flip-horizontal':card.mirrorImage}" alt="" />
            <header class="card-header">
              <div class="card-info">
                <h3 class="card-title font-title">{{ card.name }}</h3>
                <p v-if="!card.splitRole" class="card-role font-subtitle">{{ card.role }}</p>
                <p v-else class="card-role font-subtitle" v-html="card.role"></p>
              </div>
              <nuxt-link
                v-if="card.linkedin"
                :href="card.linkedin"
                class="card-link"
                target="_blank"
                ><icon
                  class="card-icon"
                  size="40px"
                  name="icons:linkedin4"
                  mode="svg"
              /></nuxt-link>
            </header>
            <nuxt-img
              :src="card.hoverImage"
              class="card-image-hover card-image-hover-1"
              alt=""
            />
            <ul class="card-list font-bodytext card-list-1">
              <li class="card-item">
                <icon
                  class="card-bullet"
                  size="22px"
                  name="icons:team-bullet"
                />
                <p><span>Project(s):</span> {{ card.projects }}</p>
              </li>
              <li class="card-item">
                <icon
                  class="card-bullet"
                  size="22px"
                  name="icons:team-bullet"
                />
                <p>
                  <span>Can’t live without:</span> {{ card.cannotLiveWithout }}
                </p>
              </li>
              <li class="card-item">
                <icon
                  class="card-bullet"
                  size="22px"
                  name="icons:team-bullet"
                />
                <p>
                  <span>Favorite song: </span>
                  <span class="font-italic">{{ card.favoriteSong }}</span
                  ><span style="font-weight: 400" v-if="card.songAuthor">
                    by </span
                  ><span v-if="card.songAuthor" class="font-italic">{{
                    card.songAuthor
                  }}</span>
                </p>
              </li>
              <li class="card-item">
                <icon
                  class="card-bullet"
                  size="22px"
                  name="icons:team-bullet"
                />
                <p><span>Dream trip:</span> {{ card.dreamTrip }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.team-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.team {
  width: 100%;
  padding: 60px 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 60px;
}

.title {
  width: 100%;
  max-width: 1440px;
  padding: 0 40px;
  text-align: left;
  text-transform: uppercase;
}

.marquee {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.cards-wrapper {
  width: fit-content;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 40px;
  animation: marquee 45s linear infinite;
}

.cards-wrapper:hover {
  animation-play-state: paused;
}

.card {
  position: relative;
  width: 325px;
  height: 380px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}

.card-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  z-index: 1;
}

.card-header {
  z-index: 2;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.card-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.card-title {
  padding: 4px 8px;
  border-radius: 4px;
  background-color: var(--color-white);
}

.card-role {
  padding: 4px 8px;
  border-radius: 4px;
  background-color: var(--color-white);
  color: var(--main-blue);
  text-transform: uppercase;
}
.img-flip-horizontal {
  transform: scaleX(-1);
}

.card-link,
.card-link span {
  cursor: pointer;
}

.card-icon {
  transition: all 0.3s ease;
}

.card-link:active .card-icon {
  filter: blur(0);
  transform: scale(0.97);
}

.card-icon {
  transition: filter 0.3s ease;
  filter: blur(0.7px);
}

.card:hover .card-icon {
  filter: blur(0);
}

.card-image-hover {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  z-index: 3;
  opacity: 0;
  transform: translateY(100px);
  transition: opacity 0.3s ease, transform 0.4s ease;
}

.card:hover .card-image-hover {
  opacity: 1;
  transform: translateY(0);
}

.card-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: 4;
  color: var(--color-white);
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.card:hover .card-list {
  opacity: 1;
  transform: translateY(0);
}

.card-item {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
}

.card-item span {
  font-weight: 600;
}

.card-item span.font-italic {
  font-weight: 400;
  font-style: italic;
}

.card-bullet {
  filter: blur(0.7px);
  flex-shrink: 0;
}

@keyframes marquee {
  0% {
    transform: translate(0, 0);
  }

  100% {
    transform: translate(-5110px, 0);
  }
}

@media (max-width: 1200px) {
  .title {
    padding: 0 20px;
  }
}

@media (max-width: 1023px) {
  .team {
    padding: 30px 0 20px;
    gap: 32px;
  }
}
</style>
